import { NgTemplateOutlet } from '@angular/common';
import type { OnChanges, SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  TemplateRef,
} from '@angular/core';
import type { ValidationErrors } from '@angular/forms';
import { AbstractControl } from '@angular/forms';
// eslint-disable-next-line local-rules/validate-freelancer-imports -- Direct import to avoid ESM chain: @freelancer/pipes barrel → PipesModule → @freelancer/auth → CJS/ESM error in prepare:bits
import { FreelancerAsyncPipeModule } from '@freelancer/pipes/async/async-pipe.module';
import type { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { IconColor, IconModule, IconSize } from '../icon';

@Component({
  standalone: true,
  imports: [FreelancerAsyncPipeModule, IconModule, NgTemplateOutlet],
  selector: 'fl-validation-error',
  template: `
    @let statuses = this.statuses$ | flAsync;

    @if (
      statuses?.length && control.invalid && (control.touched || control.dirty)
    ) {
      @for (
        error of statuses;
        track error;
        let first = $first;
        let i = $index
      ) {
        <div class="ErrorContainer">
          @if (showIcon) {
            <fl-icon
              class="IconError mr-xxxsmall"
              [name]="'ui-warning'"
              [color]="isWarning ? IconColor.WARNING : IconColor.ERROR"
              [size]="IconSize.SMALL"
              [useIconFont]="true"
              [fillIconFont]="true"
            ></fl-icon>
          }
          <p
            class="ErrorText {{ isWarning ? 'text-warning' : 'text-error' }}"
            aria-live="polite"
            [attr.id]="
              id !== undefined ? (first ? id : id + '-' + i) : undefined
            "
          >
            @if (customValidationError !== undefined) {
              <ng-container
                *ngTemplateOutlet="customValidationError"
              ></ng-container>
            } @else {
              {{ error }}
            }
          </p>
        </div>
      }
    }
  `,
  styleUrls: ['./validation-error.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ValidationErrorComponent implements OnChanges {
  IconColor = IconColor;
  IconSize = IconSize;

  statuses$: Observable<readonly string[]>;

  @Input() id?: string;
  @Input() control: AbstractControl;
  @Input() isWarning?: boolean = false;
  /** Displays error text based on the given error key */
  @Input() errorKey?: string;
  @Input() showIcon = true;
  /** If provided, the validation error will display the custom error template instead of the default error text */
  @Input() customValidationError?: TemplateRef<HTMLElement>;
  /** Whether to show multiple error messages at once. */
  @Input() showMultipleErrors = false;

  ngOnChanges(changes: SimpleChanges): void {
    if ('control' in changes || 'showMultipleErrors' in changes) {
      this.statuses$ = this.control.statusChanges.pipe(
        startWith(this.control.status),
        map(() => {
          if (this.isMatDatePickerError(this.control.errors)) {
            return [$localize`Please select a valid date.`];
          }

          if (
            this.errorKey &&
            this.control.errors &&
            this.errorKey in this.control.errors
          ) {
            return [this.control.errors[this.errorKey] as string];
          }

          const errors: string[] = Object.values(this.control.errors || {});
          return this.showMultipleErrors ? errors : errors.slice(0, 1);
        }),
      );
    }
  }

  // Hack around Material's error format
  private isMatDatePickerError(errors: ValidationErrors | null): any {
    return (
      errors &&
      (errors.matDatepickerParse ||
        errors.matDatepickerMin ||
        errors.matDatepickerMax ||
        errors.matStartDateInvalid ||
        errors.matEndDateInvalid)
    );
  }
}
